import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Clock, AlertCircle, Smartphone, ArrowLeft, Loader2 } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { useContribution, useMember, usePublicConfig } from '../context';

const currencyFormatter = new Intl.NumberFormat('en-KE', {
  style: 'currency',
  currency: 'KES',
  maximumFractionDigits: 0,
});

const formatCurrency = (value: number) => currencyFormatter.format(value);

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso));
};

type PaymentState = 'idle' | 'initiating' | 'waiting' | 'success' | 'failed';

export const ContributionPage = () => {
  const { currentContribution, loading, error, initiateMpesaPayment, processing, refreshContribution } = useContribution();
  const { dashboard } = useMember();
  const { config: publicConfig } = usePublicConfig();
  const [paymentState, setPaymentState] = useState<PaymentState>('idle');
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
  const [checkoutRequestId, setCheckoutRequestId] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttempts = useRef(0);
  const MAX_POLLING_ATTEMPTS = 24; // 24 attempts * 5s = 120 seconds (2 minutes)

  const chamaName = publicConfig?.name ?? 'LoopFund';
  const user = dashboard?.user;
  const config = dashboard?.chama_config;

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Poll for payment status
  const startPolling = async (requestId: string) => {
    pollingAttempts.current = 0;
    setPaymentState('waiting');
    setPaymentMessage('Check your phone and enter your M-Pesa PIN to complete payment...');
    setCheckoutRequestId(requestId);

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Import the API
    const { ContributionApi } = await import('../services');

    pollingIntervalRef.current = setInterval(async () => {
      pollingAttempts.current += 1;

      try {
        // Query M-Pesa STK status
        const stkStatus = await ContributionApi.querySTKStatus(requestId);
        
        console.log('[Polling] STK Status:', stkStatus, 'Attempt:', pollingAttempts.current);

        // Check M-Pesa response codes
        // ResultCode '0' = Success, '1' = User cancelled, '1032' = Timeout, etc.
        // No ResultCode or "still processing" = Transaction pending, keep waiting
        
        if (stkStatus.ResultCode === '0') {
          // Payment successful
          setPaymentState('success');
          setPaymentMessage('Payment successful! Your contribution has been recorded.');
          await refreshContribution(); // Refresh to update UI
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          return;
        }
        
        // Check if it's explicitly a failure (not just pending)
        const isPending = !stkStatus.ResultCode || 
                         stkStatus.ResultDesc?.toLowerCase().includes('still') ||
                         stkStatus.ResultDesc?.toLowerCase().includes('processing') ||
                         stkStatus.ResultDesc?.toLowerCase().includes('pending');
        
        if (stkStatus.ResultCode && !isPending) {
          // Payment explicitly failed (user cancelled, timeout, insufficient funds, etc.)
          setPaymentState('failed');
          const failureMessage = stkStatus.ResultDesc || 'Payment was not completed. Please try again.';
          console.log('[Polling] Payment failed:', failureMessage);
          setPaymentMessage(failureMessage);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          return;
        }

        // Still pending - update message to show we're waiting
        if (pollingAttempts.current % 3 === 0) { // Update message every 15 seconds
          setPaymentMessage(
            `Waiting for payment confirmation... (${Math.floor(pollingAttempts.current * 5 / 60)} min ${(pollingAttempts.current * 5) % 60} sec)`
          );
        }

        // Check if contribution was marked as paid (callback received)
        await refreshContribution();
        if (currentContribution?.status === 'paid') {
          setPaymentState('success');
          setPaymentMessage('Payment successful! Your contribution has been recorded.');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          return;
        }

        // Stop polling after max attempts
        if (pollingAttempts.current >= MAX_POLLING_ATTEMPTS) {
          setPaymentState('failed');
          setPaymentMessage('Payment verification timed out. If you completed the payment, it will be reflected shortly.');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
        }
      } catch (err) {
        console.error('[Polling] Error querying payment status:', err);
        
        // On error, still check contribution status as fallback
        try {
          await refreshContribution();
          if (currentContribution?.status === 'paid') {
            setPaymentState('success');
            setPaymentMessage('Payment successful! Your contribution has been recorded.');
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }
          }
        } catch (refreshErr) {
          console.error('[Polling] Error refreshing contribution:', refreshErr);
        }
      }
    }, 5000); // Poll every 5 seconds (avoid rate limiting)
  };

  const handlePayment = async () => {
    setPaymentState('initiating');
    setPaymentMessage(null);

    console.log('[Payment] Initiating M-Pesa payment for amount:', currentContribution?.amount);

    try {
      const result: { success: boolean; message: string; checkout_request_id?: string } = 
        await initiateMpesaPayment(currentContribution?.amount);

      console.log('[Payment] Payment initiation result:', result);

      if (result.success) {
        const checkoutId = result.checkout_request_id;
        
        if (!checkoutId) {
          throw new Error('No checkout request ID received from M-Pesa');
        }

        console.log('[Payment] Starting polling for checkout ID:', checkoutId);
        setPaymentMessage(result.message);
        await startPolling(checkoutId);
      } else {
        setPaymentState('failed');
        console.error('[Payment] Payment initiation failed:', result.message);
        setPaymentMessage(result.message || 'Failed to initiate payment. Please try again.');
      }
    } catch (error) {
      setPaymentState('failed');
      const errorMessage = error instanceof Error ? error.message : 'Failed to initiate payment';
      console.error('[Payment] Payment error:', errorMessage);
      setPaymentMessage(errorMessage);
    }
  };

  const resetPayment = () => {
    setPaymentState('idle');
    setPaymentMessage(null);
    setCheckoutRequestId(null);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
  };

  if (loading && !currentContribution) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
            <p className="text-sm font-medium text-slate-500 dark:text-slate-300">
              Loading contribution details...
            </p>
          </div>
        </div>
      </AnimatedSection>
    );
  }

  if (error) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
            <h2 className="font-display text-xl">Failed to load contribution</h2>
            <p className="mt-2 text-sm">{error}</p>
          </div>
        </div>
      </AnimatedSection>
    );
  }

  if (!currentContribution || !user || !config) {
    return (
      <AnimatedSection className="section-padding">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-center py-20">
          <p className="text-sm text-slate-500 dark:text-slate-300">No contribution data available.</p>
        </div>
      </AnimatedSection>
    );
  }

  const isPaid = currentContribution.status === 'paid';
  const isPaymentSuccess = paymentState === 'success';
  const isOverdue = currentContribution.status === 'overdue' || currentContribution.status === 'penalized';
  const isProcessing = processing || paymentState === 'initiating' || paymentState === 'waiting';
  const contributionsOpen = currentContribution.contributions_open ?? true;
  const cycleState = currentContribution.cycle_state;

  return (
    <AnimatedSection className="section-padding">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8">
        <div className="flex items-center justify-between gap-4">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-600 transition hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Link>
          <Link
            to="/history"
            className="text-sm font-semibold text-slate-600 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            View History
          </Link>
        </div>

        <header className="rounded-3xl border border-slate-200 bg-white px-8 py-10 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-brand-700 dark:text-brand-300">
                Monthly contribution
              </p>
              <h1 className="mt-3 font-display text-3xl text-slate-900 dark:text-white">
                {currentContribution.month}
              </h1>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {user.full_name} • Member ID: {user.id}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {isPaid || isPaymentSuccess ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-6 py-3 text-sm font-semibold text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  Paid
                </motion.div>
              ) : isOverdue ? (
                <div className="flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-6 py-3 text-sm font-semibold text-red-700 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-200">
                  <AlertCircle className="h-5 w-5" />
                  Overdue
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-6 py-3 text-sm font-semibold text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200">
                  <Clock className="h-5 w-5" />
                  Pending
                </div>
              )}
            </div>
          </div>
        </header>

        {!contributionsOpen && !isPaid && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-500/40 dark:bg-amber-500/10">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-6 w-6 text-amber-600 dark:text-amber-300" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                  Contributions are currently closed
                </h3>
                <p className="mt-2 text-sm text-amber-700 dark:text-amber-200">
                  {cycleState === 'setup' && 'The admin is setting up the contribution cycle. You will be notified when contributions open.'}
                  {cycleState === 'payout_ready' && 'Contributions have been closed for this period. Payouts are being processed.'}
                  {cycleState === 'cycle_active' && 'This cycle is currently active. Contributions will open for the next cycle soon.'}
                  {cycleState === 'paused' && 'Contributions are temporarily paused. Please contact the admin for more information.'}
                  {!cycleState && 'The admin has not opened contributions yet. Please check back later.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <h2 className="font-display text-xl text-slate-900 dark:text-white">Contribution details</h2>
            <dl className="mt-6 space-y-4 text-sm">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
                <dt className="text-slate-600 dark:text-slate-300">Amount due</dt>
                <dd className="text-lg font-semibold text-slate-900 dark:text-white">
                  {formatCurrency(currentContribution.amount)}
                </dd>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
                <dt className="text-slate-600 dark:text-slate-300">Due date</dt>
                <dd className="font-semibold text-slate-900 dark:text-white">
                  {currentContribution.due_date ?? '—'}
                </dd>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
                <dt className="text-slate-600 dark:text-slate-300">Cycle</dt>
                <dd className="font-semibold text-slate-900 dark:text-white">
                  Cycle {config.current_cycle}
                </dd>
              </div>
              {isPaid && (
                <>
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
                    <dt className="text-slate-600 dark:text-slate-300">Payment date</dt>
                    <dd className="font-semibold text-slate-900 dark:text-white">
                      {formatDate(currentContribution.date_paid)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
                    <dt className="text-slate-600 dark:text-slate-300">Payment method</dt>
                    <dd className="font-semibold uppercase text-slate-900 dark:text-white">
                      {currentContribution.payment_method ?? '—'}
                    </dd>
                  </div>
                  {currentContribution.transaction_id && (
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-900/70">
                      <dt className="text-slate-600 dark:text-slate-300">Transaction ID</dt>
                      <dd className="font-mono text-xs font-semibold text-slate-900 dark:text-white">
                        {currentContribution.transaction_id}
                      </dd>
                    </div>
                  )}
                </>
              )}
            </dl>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <h2 className="font-display text-xl text-slate-900 dark:text-white">Payment</h2>

            {isPaid || isPaymentSuccess ? (
              <div className="mt-6 space-y-4">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center dark:border-emerald-400/30 dark:bg-emerald-500/10">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600 dark:text-emerald-300" />
                  <p className="mt-4 font-semibold text-emerald-700 dark:text-emerald-200">
                    Payment received!
                  </p>
                  <p className="mt-2 text-sm text-emerald-600 dark:text-emerald-300">
                    Your contribution for {currentContribution.month} has been recorded. Thank you for
                    contributing to {chamaName}!
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-900/70">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-brand-100 p-3 dark:bg-brand-900/30">
                      <Smartphone className="h-6 w-6 text-brand-600 dark:text-brand-300" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 dark:text-white">Pay via M-Pesa</h3>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                        Click the button below to receive an M-Pesa STK push notification on your phone.
                        Enter your PIN to complete the payment.
                      </p>
                      <button
                        onClick={handlePayment}
                        disabled={isProcessing || !contributionsOpen}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-400"
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {paymentState === 'waiting' ? 'Waiting for confirmation...' : 'Processing...'}
                          </>
                        ) : !contributionsOpen ? (
                          <>
                            <AlertCircle className="h-4 w-4" />
                            Contributions Closed
                          </>
                        ) : (
                          <>
                            <Smartphone className="h-4 w-4" />
                            Pay {formatCurrency(currentContribution.amount)} via M-Pesa
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {paymentState === 'waiting' && paymentMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm dark:border-blue-400/30 dark:bg-blue-500/10"
                    >
                      <div className="flex items-start gap-3">
                        <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-blue-600 dark:text-blue-300" />
                        <div>
                          <p className="font-semibold text-blue-700 dark:text-blue-200">Waiting for payment</p>
                          <p className="mt-1 text-blue-600 dark:text-blue-300">{paymentMessage}</p>
                          <p className="mt-2 text-xs text-blue-500 dark:text-blue-400">
                            This may take up to 60 seconds. Don't close this page.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {(paymentState as PaymentState) === 'success' && paymentMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm dark:border-emerald-400/30 dark:bg-emerald-500/10"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600 dark:text-emerald-300" />
                        <div>
                          <p className="font-semibold text-emerald-700 dark:text-emerald-200">Payment successful!</p>
                          <p className="mt-1 text-emerald-600 dark:text-emerald-300">{paymentMessage}</p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {paymentState === 'failed' && paymentMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm dark:border-red-400/30 dark:bg-red-500/10"
                    >
                      <div className="flex items-start gap-3">
                        <AlertCircle className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-300" />
                        <div className="flex-1">
                          <p className="font-semibold text-red-700 dark:text-red-200">Payment failed</p>
                          <p className="mt-1 text-red-600 dark:text-red-300">{paymentMessage}</p>
                          <button
                            onClick={resetPayment}
                            className="mt-3 text-sm font-semibold text-red-700 underline hover:text-red-800 dark:text-red-200 dark:hover:text-red-100"
                          >
                            Try again
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                  <p className="font-semibold text-slate-900 dark:text-white">Need help?</p>
                  <p className="mt-2">
                    If you experience issues with M-Pesa payments, please contact{' '}
                    <span className="font-semibold">{chamaName}</span> support.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {currentContribution.message && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
            <p className="font-semibold text-slate-900 dark:text-white">Note</p>
            <p className="mt-2">{currentContribution.message}</p>
          </div>
        )}
      </div>
    </AnimatedSection>
  );
};
